import { useQuery } from '@tanstack/react-query';
import { firstValueFrom } from 'rxjs';
import { useResourceBookingServiceClient } from 'hooks/use-resource-booking-service-client.hook';
import { Floorplan } from 'floorplan/pages/native-floorplan/types';
import { resolveFloorplanImageUrl } from 'floorplan/pages/native-floorplan/utils/floorplan-image-url';

// Floorplans are authored by an admin and change rarely, so a few minutes of freshness avoids a
// refetch every time the floorplan page remounts.
const STALE_TIME = 5 * 60 * 1000;

// Stable reference for the disabled / not-yet-loaded case.
const EMPTY: Floorplan[] = [];

export const FLOORPLANS_QUERY_KEY = 'floorplans';

interface UseFloorplansQueryParams {
  /**
   * The building to fetch floorplans for — the currently selected location, not necessarily the
   * building the miniapp launched into. Callers resolve this themselves (see `useFloorplanViewState`)
   * so this hook stays a plain scoped query and refetches whenever the caller's scope changes, since
   * it is part of the query key.
   */
  buildingUuid?: string | null;
  enabled?: boolean;
}

export interface UseFloorplansQueryResult {
  floorplans: Floorplan[];
  isLoading: boolean;
  isError: boolean;
  /** Loaded successfully, but this building has no floorplan authored yet. */
  isEmpty: boolean;
  refetch: VoidFunction;
}

/**
 * A building's floorplans, ordered by `sortOrder` ascending. In the native view a floorplan is
 * the floor, so this backs both the floor switcher and the image the map is drawn on, and supplies
 * the `floorplanUuid` that `useFloorplanMapQuery` needs.
 */
export const useFloorplansQuery = ({
  buildingUuid,
  enabled = true,
}: UseFloorplansQueryParams = {}): UseFloorplansQueryResult => {
  const { getClient, apiUrl } = useResourceBookingServiceClient();

  const isEnabled = enabled && !!buildingUuid;

  const query = useQuery({
    queryKey: [FLOORPLANS_QUERY_KEY, buildingUuid],
    queryFn: async (): Promise<Floorplan[]> => {
      const client = getClient();
      const floorplans = await firstValueFrom(client.getFloorplans(buildingUuid as string));

      // BE documents the list as sorted, but the client contract is just an array — sorting here
      // makes this hook's own ordering promise hold whatever the response order turns out to be.
      // `sort` is stable, so floorplans sharing a sortOrder keep their server order.
      return floorplans
        .map(({ uuid, name, imageUrl, imageWidth, imageHeight, sortOrder }) => ({
          uuid,
          name,
          // Stored value can be a Helix path; consumers get a loadable `<img src>`.
          imageUrl: resolveFloorplanImageUrl(imageUrl, apiUrl),
          imageWidth,
          imageHeight,
          sortOrder,
        }))
        .sort((left, right) => left.sortOrder - right.sortOrder);
    },
    enabled: isEnabled,
    staleTime: STALE_TIME,
  });

  const floorplans = query.data ?? EMPTY;

  return {
    floorplans,
    isLoading: isEnabled && query.isLoading,
    isError: query.isError,
    isEmpty: query.isSuccess && floorplans.length === 0,
    refetch: query.refetch,
  };
};
